import React, { useCallback, useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'

import useSettings from '@/hooks/useSettings'
import KorektyService from '@/services/KorektyService'

import useKorektaFormState from '../common/useKorektaFormState'
import NotesSection from '../common/NotesSection'
import DateDiscountTermSection from '../common/DateDiscountTermSection'
import CorrectionProductTable from '../common/CorrectionProductTable'
import TotalsSection from '../common/TotalsSection'
import {
	appendInfaktCustomerToPayload,
	buildInfaktCustomerFromInvoice,
	buildPayload,
	updateKorektaOnServer,
} from '../common/submitKorekta'
import { buildFormFromInvoice } from '../common/korektaLoadHelpers'

const LOAD_FAILED_KEY = 'korekty.edit.messages.loadFailed'
const UPDATE_FAILED_KEY = 'korekty.edit.messages.updateFailed'

export default function KorektaEdit() {
	const { id } = useParams()
	const navigate = useNavigate()
	const { t } = useTranslation()
	const { settings } = useSettings()
	const currencySymbol = settings?.companyCurrencySymbol?.trim() || 'PLN'
	const fmtMoney = n => `${currencySymbol} ${Number(n ?? 0).toFixed(2)}`

	const { form, setForm, setField, setAfterField } = useKorektaFormState()
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [submitting, setSubmitting] = useState(false)
	const [sourceKorekta, setSourceKorekta] = useState(null)

	useEffect(() => {
		let alive = true
		;(async () => {
			setLoading(true)
			setError(null)
			try {
				const { data, error: loadError } = await KorektyService.getKorektaById(id)
				if (!alive) return
				if (loadError) throw new Error(loadError || LOAD_FAILED_KEY)

				if (data?.infaktInvoiceUuid || data?.infakt_invoice_uuid) {
					navigate(`/ksiegowosc/korekty/details/${id}`, { replace: true })
					return
				}

				setSourceKorekta(data)
				setForm(buildFormFromInvoice(data))
			} catch (e) {
				setError(e.message || LOAD_FAILED_KEY)
			} finally {
				if (alive) setLoading(false)
			}
		})()
		return () => { alive = false }
	}, [id, navigate, setForm])

	const handleSave = useCallback(async e => {
		e?.preventDefault()
		if (submitting) return
		setSubmitting(true)
		setError(null)

		try {
			const payload = buildPayload(form)
			appendInfaktCustomerToPayload(payload, buildInfaktCustomerFromInvoice(sourceKorekta))
			const { success, error: apiError } = await updateKorektaOnServer(KorektyService, id, payload)
			if (!success) throw new Error(apiError || t(UPDATE_FAILED_KEY))
			navigate(`/ksiegowosc/korekty/details/${id}`)
		} catch (err) {
			setError(err.message || UPDATE_FAILED_KEY)
			await Swal.fire({
				icon: 'error',
				title: t('korekty.add.messages.errorTitle'),
				text: err.message || t(UPDATE_FAILED_KEY),
			})
		} finally {
			setSubmitting(false)
		}
	}, [form, id, navigate, sourceKorekta, submitting, t])

	if (loading) {
		return (
			<div className="min-h-[40vh] flex items-center justify-center text-gray-600">
				{t('korekty.edit.loading')}
			</div>
		)
	}

	return (
		<div className="max-w-6xl mx-auto px-0 sm:px-6 lg:px-8">
			<form onSubmit={handleSave} className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
				<div className="px-4 sm:px-6 py-6 border-b border-gray-200">
					<h2 className="text-2xl font-bold text-gray-900">
						{t('korekty.edit.title', { number: form.invoiceNumber ?? id?.slice(-6) ?? '—' })}
					</h2>
					<p className="text-sm text-gray-500 mt-1">
						{form.companyName || form.customerName || '-'}
						{form.sourceInvoiceId && (
							<>
								{' · '}
								<Link
									to={`/ksiegowosc/invoices/details/${form.sourceInvoiceId}`}
									className="text-blue-600 hover:underline">
									{form.correctedInvoiceNumber}
								</Link>
							</>
						)}
					</p>
				</div>

				{error && (
					<div className="mx-4 sm:mx-6 mt-6 rounded-lg bg-red-50 p-4 border border-red-200 text-sm text-red-700">
						{t(error, { defaultValue: error })}
					</div>
				)}

				<div className="px-4 sm:px-6 py-6 space-y-6">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								{t('korekty.list.table.company')}
							</label>
							<input
								className="block w-full rounded-lg border-0 py-2.5 px-3.5 ring-1 ring-inset ring-gray-300 bg-gray-50"
								value={form.companyName || '—'}
								readOnly
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								{t('korekty.form.correctedInvoiceNumber')}
							</label>
							<input
								className="block w-full rounded-lg border-0 py-2.5 px-3.5 ring-1 ring-inset ring-gray-300 bg-gray-50"
								value={form.correctedInvoiceNumber || ''}
								readOnly
							/>
						</div>
					</div>

					<DateDiscountTermSection form={form} setField={setField} hideCorrectedNumber />

					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							{t('korekty.form.correctionReason')}
						</label>
						<select
							value={form.correctionReason || ''}
							onChange={e => setField('correctionReason', e.target.value)}
							className="block w-full rounded-lg border-0 py-2.5 px-3.5 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 text-sm"
							required>
							<option value="">{t('korekty.form.correctionReasonPlaceholder')}</option>
							<option value="accounting_error">{t('korekty.form.correctionReasons.accounting_error')}</option>
							<option value="other">{t('korekty.form.correctionReasons.other')}</option>
						</select>
					</div>

					<div>
						<h3 className="text-sm font-semibold text-gray-900 mb-2">{t('korekty.form.products.title')}</h3>
						<p className="text-xs text-gray-500 mb-3">{t('korekty.form.products.hint')}</p>
						<CorrectionProductTable
							products={form.products}
							setAfterField={setAfterField}
							fmtMoney={fmtMoney}
						/>
					</div>

					<NotesSection form={form} setField={setField} />
					<TotalsSection form={form} fmtMoney={fmtMoney} />
				</div>

				<div className="px-4 sm:px-6 py-6 border-t border-gray-200 flex gap-3">
					<button
						type="submit"
						disabled={submitting}
						className={`px-5 py-2 rounded text-white ${submitting ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
						{submitting ? t('korekty.edit.submitting') : t('korekty.edit.submit')}
					</button>
					<button
						type="button"
						onClick={() => navigate(`/ksiegowosc/korekty/details/${id}`)}
						className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
						{t('korekty.edit.cancel')}
					</button>
				</div>
			</form>
		</div>
	)
}
